import { Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, OnDestroy, OnInit, Optional, Renderer2 } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { AuthService, FeatureConfigService, WindowRef } from '@spartacus/core';
import { HamburgerMenuService, NavigationNode } from '@spartacus/storefront';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { ICON_TYPE } from '../../../../../../custom/icons/icon.model';

@Component({
  selector: 'app-custom-navigation-ui',
  templateUrl: './custom-navigation-ui.component.html',
  styleUrls: ['./custom-navigation-ui.component.scss'],
  standalone: false
})
export class CustomNavigationUiComponent implements OnDestroy, OnInit {
  protected subscriptions: Subscription = new Subscription();
  @Input() node: NavigationNode;
  @Input() isMenu = false;

  /**
   * The number of child nodes that must be wrapped.
   */
  @Input() wrapAfter: number;
  /**
   * the icon type that will be used for navigation nodes
   * with children.
   */
  iconType = ICON_TYPE;

  /**
   * Indicates whether the navigation should support flyout.
   * If flyout is set to true, the
   * nested child navigation nodes will only appear on hover or focus.
   */
  @Input() @HostBinding('class.flyout') flyout = true;

  @Input() @HostBinding('class.is-open') isOpen = false;

  private openNodes: HTMLElement[] = [];
  private resize = new EventEmitter();
  private isLogged: Observable<boolean>;

  @HostListener('window:resize')
  onResize(): void {
    this.resize.next(undefined);
  }

  constructor(
    private router: Router,
    private renderer: Renderer2,
    private elemRef: ElementRef,
    private auth: AuthService,
    protected winRef: WindowRef,
    protected customHamburgerMenuService: HamburgerMenuService,
    @Optional() protected featureConfigService?: FeatureConfigService
  ) {
    this.subscriptions.add(this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => this.clear()));
    this.subscriptions.add(
      this.resize.pipe(debounceTime(50)).subscribe(() => {
        this.alignWrappersToRightIfStickOut();
      })
    );
  }

  toggleOpen(event: UIEvent): void {
    if (event.type === 'keydown') {
      event.preventDefault();
    }
    const node = event.currentTarget as HTMLElement;
    if (this.openNodes.includes(node)) {
      if (event.type === 'keydown') {
        this.back();
      } else {
        this.openNodes = this.openNodes.filter((n) => n !== node);
        this.renderer.removeClass(node, 'is-open');
      }
    } else {
      this.openNodes.push(node);
      this.renderer.setAttribute(node, 'aria-expanded', 'true');
    }

    this.updateClasses();

    event.stopImmediatePropagation();
    event.stopPropagation();
  }

  back(): void {
    if (this.openNodes[this.openNodes.length - 1]) {
      this.renderer.removeClass(this.openNodes[this.openNodes.length - 1], 'is-open');
      this.openNodes.pop();
      this.updateClasses();
    }
  }

  clear(): void {
    this.openNodes = [];
    this.updateClasses();
  }

  onMouseEnter(event: MouseEvent): void {
    this.alignWrapperToRightIfStickOut(event.currentTarget as HTMLElement);
    this.focusAfterPreviousClicked(event);
  }

  getTotalDepth(node: NavigationNode, depth = 0): number {
    if (node.children && node.children.length > 0) {
      return Math.max(...node.children.map((n) => this.getTotalDepth(n, depth + 1)));
    } else {
      return depth;
    }
  }

  getColumnCount(length: number): number {
    return Math.round(length / (this.wrapAfter || length));
  }

  focusAfterPreviousClicked(event: MouseEvent): Document {
    const target: HTMLElement = (event.target || event.relatedTarget) as HTMLElement;
    if (target.ownerDocument.activeElement.matches('nav[tabindex]') && target.parentElement.matches('.flyout')) {
      target.focus();
    }
    return target.ownerDocument;
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  private alignWrapperToRightIfStickOut(node: HTMLElement): void {
    const wrapper = node.querySelector('.wrapper') as HTMLElement;
    const body = node.closest('body') as HTMLElement;
    if (wrapper) {
      this.renderer.removeStyle(wrapper, 'margin-left');
      if (wrapper.offsetLeft + wrapper.offsetWidth > body.offsetLeft + body.offsetWidth) {
        this.renderer.setStyle(wrapper, 'margin-left', `${node.offsetWidth - wrapper.offsetWidth}px`);
      }
    }
  }

  private alignWrappersToRightIfStickOut(): void {
    const navs = this.elemRef.nativeElement.childNodes as HTMLCollection;
    Array.from(navs)
      .filter((node) => node.tagName === 'NAV')
      .forEach((nav) => this.alignWrapperToRightIfStickOut(nav as HTMLElement));
  }

  private updateClasses(): void {
    this.openNodes.forEach((node, i) => {
      if (i + 1 < this.openNodes.length) {
        this.renderer.addClass(node, 'is-opened');
        this.renderer.removeClass(node, 'is-open');
      } else {
        this.renderer.removeClass(node, 'is-opened');
        this.renderer.addClass(node, 'is-open');
        this.openNodes = [node];
      }
    });

    this.isOpen = this.openNodes.length > 0;
  }

  ngOnInit(): void {
    this.isLogged = this.auth.isUserLoggedIn();
  }

  goToElement(id: string): void {
    if (id?.includes('BenefitsLink')) {
      this.winRef.localStorage.setItem('professionalElement', 'chefsarea-professional');
      const targetEl = this.winRef.document.getElementById('chefsarea-professional');
      const yOffset = -100;

      if (targetEl) {
        const y = targetEl.getBoundingClientRect().top + this.winRef.nativeWindow.pageYOffset + yOffset;
        targetEl.scrollIntoView();
        this.winRef.nativeWindow.scrollTo({ top: y });
      }
    } else if (id?.includes('newsletter-link')) {
      const targetEl = this.winRef.document.getElementById('newsletter-section');
      const inputNewsletter = this.winRef.document.getElementById('input-newsletter');
      targetEl.scrollIntoView({ behavior: 'smooth' });
      inputNewsletter.click();
      this.customHamburgerMenuService.toggle();
    }
  }
}
